import { Grid } from "@material-ui/core";
import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { CornermarkDetailsFragment } from "@saleor/fragments/types/CornermarkDetailsFragment";
import { CornermarkErrorFragment } from "@saleor/fragments/types/CornermarkErrorFragment";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListProps2, TabListActions } from "@saleor/types";
import { CornerMarkTypeEnum } from "@saleor/types/globalTypes";
import { concat } from "lodash-es";
import React from "react";
import { useIntl } from "react-intl";

import CornerMarkCategories from "../CornerMarkCategories/CornerMarkCategories";
import CornerMarkCollections from "../CornerMarkCollections";
import CornermarkDetails from "../CornermarkDetails/CornermarkDetails";
import CornerMarkProducts from "../CornerMarkProducts";
import CornerMarkVariants from "../CornerMarkVariants";

export interface CornermarkPageFormData {
  title: string;
  textColor: string;
  type: CornerMarkTypeEnum;
  mainColor: string;
  secondaryColor: string;
}
export enum CornerMarkDetailsPageTab {
  categories = "categories",
  collections = "collections",
  products = "products",
  variants = "variants"
}
export function cornerMarkDetailsPageTab(
  tab: string
): CornerMarkDetailsPageTab {
  return tab === CornerMarkDetailsPageTab.variants
    ? CornerMarkDetailsPageTab.variants
    : tab === CornerMarkDetailsPageTab.products
    ? CornerMarkDetailsPageTab.products
    : tab === CornerMarkDetailsPageTab.collections
    ? CornerMarkDetailsPageTab.collections
    : CornerMarkDetailsPageTab.categories;
}
export interface CornermarkPageProps
  extends Pick<ListProps2, Exclude<keyof ListProps2, "onRowClick">>,
    TabListActions<
      | "categoryListToolbar"
      | "collectionListToolbar"
      | "productListToolbar"
      | "variantListToolbar"
    > {
  cornermark: CornermarkDetailsFragment | null;
  disabled: boolean;
  activeTab?: CornerMarkDetailsPageTab;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onDelete: () => void;
  onSubmit: (data: CornermarkPageFormData) => void;
  errors: CornermarkErrorFragment[];
  title: string;
  onTabClick?: (index: CornerMarkDetailsPageTab) => void;
  onProductAssign: () => void;
  onProductUnassign: (id: string) => void;
  onProductClick: (id: string) => () => void;

  onVariantAssign: () => void;
  onVariantUnassign: (id: string) => void;
  onVariantClick: (productId: string, variantId: string) => () => void;

  onCollectionAssign: () => void;
  onCollectionUnassign: (id: string) => void;
  onCollectionClick: (id: string) => () => void;

  onCategoryAssign: () => void;
  onCategoryUnassign: (id: string) => void;
  onCategoryClick: (id: string) => () => void;
}
const CategoriesTab = Tab(CornerMarkDetailsPageTab.categories);
const CollectionsTab = Tab(CornerMarkDetailsPageTab.collections);
const ProductsTab = Tab(CornerMarkDetailsPageTab.products);
const VariantsTab = Tab(CornerMarkDetailsPageTab.variants);

const CornermarkPage: React.FC<CornermarkPageProps> = ({
  categoryListToolbar,
  collectionListToolbar,
  productListToolbar,
  variantListToolbar,

  onProductAssign,
  onProductUnassign,
  onProductClick,

  onVariantAssign,
  onVariantUnassign,
  onVariantClick,

  onCollectionAssign,
  onCollectionUnassign,
  onCollectionClick,

  onCategoryAssign,
  onCategoryUnassign,
  onCategoryClick,

  onNextPage,
  onPreviousPage,
  pageInfo,
  isChecked,
  selected,
  toggle,
  toggleAll,

  cornermark,
  disabled,
  onTabClick,
  errors,
  saveButtonBarState,
  activeTab,
  onBack,
  onDelete,
  onSubmit,
  title
}) => {
  const intl = useIntl();
  const initialForm: CornermarkPageFormData =
    cornermark === null
      ? {
          title: "",
          textColor: "",
          type: CornerMarkTypeEnum.NORMAL,
          mainColor: "",
          secondaryColor: ""
        }
      : {
          title: maybe(() => cornermark.titleText, ""),
          textColor: maybe(() => cornermark.textColor, ""),
          type: maybe(() => cornermark.type, CornerMarkTypeEnum.NORMAL),
          mainColor: maybe(() => cornermark.mainColor, ""),
          secondaryColor: maybe(() => cornermark.secondaryColor, "")
        };

  const handleSubmit = (data: CornermarkPageFormData) =>
    onSubmit({
      ...data
    });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.cornermarks)}
          </AppHeader>
          <PageHeader
            title={
              cornermark === null
                ? intl.formatMessage({
                    defaultMessage: "Create New Cornermark",
                    description: "create new cornermark"
                  })
                : maybe(() => concat(cornermark.titleText, " Title"))
            }
          />
          <Grid>
            <div>
              <CornermarkDetails
                errors={errors}
                data={data}
                disabled={disabled}
                onChange={change}
                title={title}
              />
            </div>
            <CardSpacer />
            {
              <>
                <TabContainer>
                  <CategoriesTab
                    isActive={activeTab === CornerMarkDetailsPageTab.categories}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage(
                      {
                        defaultMessage: "Categories ({quantity})",
                        description: "number of categories"
                      },
                      {
                        quantity: maybe(
                          () => cornermark.categories.totalCount.toString(),
                          "…"
                        )
                      }
                    )}
                  </CategoriesTab>
                  <CollectionsTab
                    isActive={
                      activeTab === CornerMarkDetailsPageTab.collections
                    }
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage(
                      {
                        defaultMessage: "Recommendation Groups ({quantity})",
                        description: "number of collections"
                      },
                      {
                        quantity: maybe(
                          () => cornermark.collections.totalCount.toString(),
                          "…"
                        )
                      }
                    )}
                  </CollectionsTab>
                  <ProductsTab
                    isActive={activeTab === CornerMarkDetailsPageTab.products}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage(
                      {
                        defaultMessage: "Products ({quantity})",
                        description: "number of products"
                      },
                      {
                        quantity: maybe(
                          () => cornermark.products.totalCount.toString(),
                          "…"
                        )
                      }
                    )}
                  </ProductsTab>
                  <VariantsTab
                    isActive={activeTab === CornerMarkDetailsPageTab.variants}
                    changeTab={onTabClick}
                  >
                    {intl.formatMessage(
                      {
                        defaultMessage: "SKU({quantity})",
                        description: "number of skus"
                      },
                      {
                        quantity: maybe(
                          () => cornermark.variants.totalCount.toString(),
                          "…"
                        )
                      }
                    )}
                  </VariantsTab>
                </TabContainer>
                <CardSpacer />
                {activeTab === CornerMarkDetailsPageTab.categories ? (
                  <CornerMarkCategories
                    disabled={disabled}
                    onCategoryAssign={onCategoryAssign}
                    onCategoryUnassign={onCategoryUnassign}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                    onRowClick={onCategoryClick}
                    pageInfo={pageInfo}
                    cornerMark={cornermark}
                    isChecked={isChecked}
                    selected={selected}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    toolbar={categoryListToolbar}
                  />
                ) : activeTab === CornerMarkDetailsPageTab.collections ? (
                  <CornerMarkCollections
                    disabled={disabled}
                    onCollectionAssign={onCollectionAssign}
                    onCollectionUnassign={onCollectionUnassign}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                    onRowClick={onCollectionClick}
                    pageInfo={pageInfo}
                    cornerMark={cornermark}
                    isChecked={isChecked}
                    selected={selected}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    toolbar={collectionListToolbar}
                  />
                ) : activeTab === CornerMarkDetailsPageTab.products ? (
                  <CornerMarkProducts
                    disabled={disabled}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                    onProductAssign={onProductAssign}
                    onProductUnassign={onProductUnassign}
                    onRowClick={onProductClick}
                    pageInfo={pageInfo}
                    cornerMark={cornermark}
                    isChecked={isChecked}
                    selected={selected}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    toolbar={productListToolbar}
                  />
                ) : (
                  <CornerMarkVariants
                    disabled={disabled}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                    onVariantAssign={onVariantAssign}
                    onVariantUnassign={onVariantUnassign}
                    onRowClick={onVariantClick}
                    pageInfo={pageInfo}
                    cornerMark={cornermark}
                    isChecked={isChecked}
                    selected={selected}
                    toggle={toggle}
                    toggleAll={toggleAll}
                    toolbar={variantListToolbar}
                  />
                )}
              </>
            }
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            state={saveButtonBarState}
            onCancel={onBack}
            onSave={submit}
            onDelete={cornermark === null ? undefined : onDelete}
          />
        </Container>
      )}
    </Form>
  );
};
export default CornermarkPage;
